// ReviewCarousel.jsx
import { useState, useEffect } from 'react';
import { Modal, Button, Rate, Input, message, Progress } from 'antd';
import { StarOutlined, MessageOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import drEasyApi from '../../Api/Customapi';
import { useLanguage } from '../../context/LanguageContext';
import { getImg } from '../../Api/getImg';

const { TextArea } = Input;

const ReviewCarousel = ({ center, user, slug }) => {
  const { t, currentLanguage } = useLanguage();
  const [pendingReviews, setPendingReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showCarousel, setShowCarousel] = useState(false);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingReviews, setFetchingReviews] = useState(true);

  const isRTL = currentLanguage === 'ar';
  const currentDoctor = pendingReviews[currentIndex];
  const totalReviews = pendingReviews.length;
  const progress = totalReviews > 0 ? ((currentIndex + 1) / totalReviews) * 100 : 0;

  // Fetch pending reviews on component mount
  useEffect(() => {
    if (user && slug) {
      fetchPendingReviews();
    }
  }, [user, slug]);

  // Pre-populate rating and text when currentDoctor changes
  useEffect(() => {
    if (currentDoctor) {
      setRating(currentDoctor.oldRating || 0);
      setReviewText(currentDoctor.oldText || '');
    }
  }, [currentDoctor]);

  const fetchPendingReviews = async () => {
    try {
      setFetchingReviews(true);
      const response = await drEasyApi.get('/api/Review/PendingReviews');
      
      if (response.data && Array.isArray(response.data) && response.data.length > 0) {
        setPendingReviews(response.data);
        setTimeout(()=>{
                  setShowCarousel(true);

        },2000)
      }
    } catch (error) {
      console.error('Error fetching pending reviews:', error);
    } finally {
      setFetchingReviews(false);
    }
  };

  const getDoctorName = (doctor) => {
    
    try {
      if (!doctor) return t('common.doctor');
      if (typeof doctor.doctorName === 'string' && doctor.doctorName.trim() !== '') {
        return doctor.doctorName;
      }
    
      return doctor.doctorName || t('common.doctor');
    } catch {
      return t('common.doctor');
    }
  };

  const handleSubmitReview = async () => {
    if (rating === 0) {
      message.warning(t('review.pleaseSelectRating'));
      return;
    }

    try {
      setLoading(true);
      const payload = {
        text: reviewText,
        rating,
        // centerId: center?.id||null,
        doctorId: currentDoctor.doctorId,
      };
      
      await drEasyApi.post('/api/Review/AddReview', payload);
      message.success(t('review.submittedSuccessfully'));
      
      // Move to next review or close carousel
      handleNextOrClose();
    } catch (error) {
      message.error(error.response?.data?.message || t('review.failedToSubmit'));
    } finally {
      setLoading(false);
    }
  };

  const handleNextOrClose = () => {
    // Remove current doctor from pending list
    const updatedReviews = pendingReviews.filter((_, idx) => idx !== currentIndex);
    setPendingReviews(updatedReviews);

    // If no more reviews, close carousel
    if (updatedReviews.length === 0) {
      setShowCarousel(false);
      setCurrentIndex(0);
      setRating(0);
      setReviewText('');
    } else {
      // Stay at same index (which now shows next doctor)
      // The useEffect will handle setting rating and text
      setCurrentIndex(Math.min(currentIndex, updatedReviews.length - 1));
    }
  };

  const handleSkip = () => {
    // Move to next doctor or loop to first
    if (currentIndex < pendingReviews.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
        pendingReviews.length<2&&setShowCarousel(false);

    }
    // Rating and text will be set by useEffect
  };

  const handleSkipAll = () => {
    setShowCarousel(false);
    setCurrentIndex(0);
    setRating(0);
    setReviewText('');
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      // Rating and text will be set by useEffect
    }
  };

  const getRatingLabel = (ratingValue) => {
    const labels = {
      1: t('review.ratings.poor'),
      2: t('review.ratings.fair'),
      3: t('review.ratings.good'),
      4: t('review.ratings.veryGood'),
      5: t('review.ratings.excellent')
    };
    return labels[ratingValue] || '';
  };

  if (!currentDoctor || !showCarousel) return null;

  const doctorName = String(getDoctorName(currentDoctor) || t('common.doctor'));

  return (
    <Modal 
    style={{direction:isRTL?"rtl":"ltr"}}
      open={showCarousel}
      title={
        <div className="space-y-3">
          <div className={`flex items-center gap-3 text-2xl font-semibold `}>
            <StarOutlined className="goldy" />
            <span>{t('review.title', { name: doctorName })}</span>
          </div>
          {/* Progress Bar */}
          <div className="space-y-2">
            <div className={`flex justify-between text-sm text-gray-600 `}>
              <span>{t('review.reviewProgress', { current: currentIndex + 1, total: totalReviews })}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <Progress 
              percent={progress} 
              showInfo={false} 
              strokeColor="#faad14"
              trailColor="#f0f0f0"
            />
          </div>
        </div>
      }
      footer={[
        <div key="footer" className={`flex gap-2  `}>
          <Button
            key="skipAll"
            size="large"
            onClick={handleSkipAll}
            disabled={loading}
          >
            {t('review.skipAll')}
          </Button>
          {currentIndex > 0 && (
            <Button
              key="previous"
              size="large"
              onClick={handlePrevious}
              disabled={loading}
              icon={isRTL ? <ArrowRightOutlined /> : <ArrowLeftOutlined />}
            >
              {t('review.previous')}
            </Button>
          )}
          {currentIndex<pendingReviews.length-1&&
          <Button
            key="skip"
            size="large"
            onClick={handleSkip}
            disabled={loading}
          >
            {t('review.skipThisOne')}
          </Button>}
          <Button
            key="submit"
            type=""
            size="large"
            onClick={handleSubmitReview}
            loading={loading}
            className="bg-goldy text-white"
            icon={<StarOutlined />}
          >
            {t('review.submitReview')}
          </Button>
        </div>,
      ]}
      onCancel={handleSkipAll}
      width={700}
      centered
      maskClosable={false}
      direction={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="py-6 space-y-8" dir={isRTL ? 'rtl' : 'ltr'}>
        {/* Doctor Info Card */}
        <div className="bg-primary/10 px-6 py-3 rounded-xl border border-blue-100">
          <div className={`flex items-center gap-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            {currentDoctor.doctorPhoto && (
              <img
                src={getImg(currentDoctor.doctorPhoto)}
                alt={doctorName}
                className="w-20 h-20 rounded-full object-cover shadow-lg border-2 border-secondary"
              />
            )}
            <div className={`flex-1 ${isRTL ? 'text-right' : 'text-left'}`}>
              <h4 className="text-xl font-semibold text-gray-800">
                {t("common.dr")} {doctorName}
              </h4>
              <p className="text-base text-gray-600 mt-1">
                {String(currentDoctor.doctorSpecialty || currentDoctor.specialty || t('review.generalPractice'))}
              </p>
              {/* {currentDoctor.lastReviewDate && (
                <p className="text-sm text-gray-500 mt-2">
                  {t('review.lastVisit')}: {new Date(currentDoctor.lastReviewDate).toLocaleDateString()}
                </p>
              )} */}
            </div>
          </div>
        </div>

        {/* Rating Section */}
        <div className="text-center bg-white p-6 rounded-xl border border-gray-100">
          <h3 className="text-xl font-medium text-gray-800 mb-4">
            {currentDoctor.oldRating?t('review.anotherRate'):t('review.howWouldYouRate')}
          </h3>
          <Rate
            value={rating}
            onChange={setRating}
            style={{ fontSize: '3rem' }}
            className="text-yellow-500"
          />
          {rating > 0 && (
            <div className="mt-3 text-lg text-gray-600 font-medium">
              {getRatingLabel(rating)}
            </div>
          )}
        </div>

        {/* Review Text Section */}
        <div>
          <label className={`block text-base font-medium text-gray-800 mb-3 `}>
            {currentDoctor.oldText?t("review.editYourExperience"):t('review.shareYourExperience')} 
            {/* ({t('review.optional')}) */}
          </label>
          <TextArea
            value={reviewText}
            onChange={(e) => setReviewText(e.target.value)}
            placeholder={t('review.tellOthers', { name: doctorName })}
            rows={5}
            maxLength={500}
            showCount
            className={`resize-none text-base p-3 rounded-lg `}
            dir={isRTL ? 'rtl' : 'ltr'}
          />
        </div>

        {/* Helpful Message */}
        {/* <div className={`flex items-start gap-3 bg-yellow-50 p-4 rounded-lg border border-yellow-100 `}>
          <MessageOutlined className="text-yellow-600 text-lg mt-1" />
          <p className={`text-sm text-gray-700 `}>
            {t('review.helpsOthers')}
          </p>
        </div> */}
      </div>
    </Modal>
  );
};

export default ReviewCarousel;